import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip, ConfidenceBadge } from "@/components/Bits";
import SourceDocViewer from "@/components/SourceDocViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ComboSelect from "@/components/ComboSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function PurchaseDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();

    const [pi, setPi] = useState(null);
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [transporters, setTransporters] = useState([]);
    const [paymentLedgers, setPaymentLedgers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [assetCategories, setAssetCategories] = useState([]);
    const [groups, setGroups] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [recurringMatches, setRecurringMatches] = useState([]);
    const [recurringDismissed, setRecurringDismissed] = useState(false);

    const load = async () => {
        const [pRes, catL, vendL, transL, allL, locs, deps] = await Promise.all([
            api.get(`/purchase-invoices/${id}`),
            api.get("/ledgers", { params: { q: "" } }),
            api.get("/ledgers", { params: { party_type: "vendor" } }),
            api.get("/ledgers", { params: { party_type: "transporter" } }),
            api.get("/ledgers"),
            api.get("/masters/locations"),
            api.get("/masters/departments"),
        ]);
        setPi(pRes.data);
        // filter category ledgers to expense/direct groups AND annotate each
        // with its group's category so the UI can render the direct/indirect
        // chip synchronously (no extra state lookup).
        const grps = (await api.get("/account-groups")).data;
        setGroups(grps);
        const groupCatBy = new Map(grps.map((g) => [g.id, g.category]));
        setCategories(
            catL.data
                .filter((l) => {
                    const c = groupCatBy.get(l.group_id);
                    return c === "direct_cost" || c === "indirect_expenses";
                })
                .map((l) => ({ ...l, _group_category: groupCatBy.get(l.group_id) }))
        );
        setVendors(vendL.data);
        setTransporters(transL.data);
        setPaymentLedgers(allL.data.filter((l) => l.party_type === "bank" || l.party_type === "cash"));
        setLocations(locs.data);
        setDepartments(deps.data);
        const p = pRes.data;
        setForm({
            vendor_ledger_id: p.vendor_ledger_id || "",
            vendor_name: p.vendor_name || "",
            vendor_gstin: p.vendor_gstin || "",
            category_ledger_id: p.category_ledger_id || "",
            invoice_no: p.invoice_no || "",
            invoice_date: p.invoice_date || "",
            subtotal: p.subtotal || 0,
            gst_amount: p.gst_amount || 0,
            cgst_amount: p.cgst_amount ?? p.extracted?.cgst ?? 0,
            sgst_amount: p.sgst_amount ?? p.extracted?.sgst ?? 0,
            igst_amount: p.igst_amount ?? p.extracted?.igst ?? 0,
            total: p.total || 0,
            lines: p.lines || [],
            inward_transport_amount: p.inward_transport_amount ?? p.inward_transport?.amount ?? null,
            inward_transport_transporter_ledger_id: p.inward_transport_transporter_ledger_id ?? p.inward_transport?.transporter_ledger_id ?? "",
            inward_transport_transporter_name: p.inward_transport_transporter_name ?? p.inward_transport?.transporter_name ?? "",
            inward_transport_rcm: p.inward_transport_rcm ?? p.inward_transport?.rcm ?? false,
            dept: p.dept || "",
            location: p.location || "",
            direct_indirect: p.direct_indirect || "direct",
            is_expense_not_purchase: p.is_expense_not_purchase || false,
            no_vendor_ledger: p.no_vendor_ledger || false,
            payment_ledger_id: p.payment_ledger_id || "",
            is_service_purchase: p.is_service_purchase || false,
            vendor_type: p.vendor_type || "",
            tds_rate: p.tds_rate ?? 0,
            tds_amount: p.tds_amount ?? 0,
            tds_ledger_id: p.tds_ledger_id || "",
            rcm_applies: p.rcm_applies || false,
            rcm_rate: p.rcm_rate ?? 0,
            // Mark-as-Asset (capex) fields
            is_asset: p.is_asset || false,
            asset_category_id: p.asset_category_id || "",
            asset_name: p.asset_name || "",
            asset_put_to_use_date: p.asset_put_to_use_date || "",
            itc_claimed_on_asset: p.itc_claimed_on_asset ?? true,
        });
        // Load asset categories once, lazily (only the first time we need them)
        try {
            if (!p.__cats_loaded) {
                const cats = await api.get("/asset-categories");
                setAssetCategories(cats.data || []);
            }
        } catch { /* asset categories are optional — silently ignore */ }
    };
    useEffect(() => { load(); }, [id]);

    // Poll while AI OCR is still running (status='extracting'). Re-fetches
    // every 2.5 s and stops as soon as the record flips to draft/failed/…
    useEffect(() => {
        if (pi?.status !== "extracting") return;
        const t = setInterval(() => { load(); }, 2500);
        return () => clearInterval(t);
    }, [pi?.status]);

    // Fetch fuzzy recurring-expense matches whenever vendor / total settles.
    // Debounced (500 ms) — user is still typing / editing lines.
    useEffect(() => {
        if (!pi || pi.status === "posted" || pi.status === "reversed" || pi.status === "cancelled") {
            setRecurringMatches([]);
            return;
        }
        const vname = form?.vendor_name || pi.vendor_name || "";
        const total = Number(form?.total) || Number(pi.total) || 0;
        if (!vname && total <= 0) { setRecurringMatches([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/recurring-match", {
                    params: {
                        vendor_name: vname,
                        total,
                        invoice_date: form?.invoice_date || pi.invoice_date || "",
                    },
                });
                setRecurringMatches(data.matches || []);
            } catch { setRecurringMatches([]); }
        }, 500);
        return () => clearTimeout(t);
    }, [pi, form?.vendor_name, form?.total, form?.invoice_date]);

    const linkToRecurringCycle = async (match) => {
        if (!match.matched_cycle_id) {
            return toast.error("This match has no active prepaid cycle to link — record the cycle first from Recurring expenses.");
        }
        try {
            await api.post(
                `/recurring-expenses/${match.recurring_expense_id}/prepaid-cycles/${match.matched_cycle_id}/link-purchase`,
                { purchase_invoice_id: id },
            );
            toast.success(`Linked to "${match.name}" prepaid cycle ${match.matched_cycle_period}. Now discard this draft to avoid double-booking.`);
            setRecurringDismissed(true);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (!pi || !form) return <div className="p-8 text-stone-500">Loading…</div>;

    // Full-screen "AI is reading your invoice…" state while OCR is running.
    if (pi.status === "extracting") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50 p-8" data-testid="pi-extracting">
                <div className="bg-white border rounded-md p-8 max-w-md w-full text-center space-y-4"
                     style={{ borderColor: "var(--se-border)" }}>
                    <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                         style={{ background: "var(--se-brand-soft)" }}>
                        <svg className="animate-spin" width="32" height="32" viewBox="0 0 24 24" fill="none"
                             stroke="var(--se-brand)" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                    </div>
                    <h2 className="font-serif text-2xl">Reading your invoice…</h2>
                    <p className="text-sm text-stone-500">
                        AI is extracting vendor, GSTIN, line items, HSN, and totals.
                        Usually takes <b>20-60 seconds</b> — up to 90 for high-res PDFs.
                    </p>
                    <p className="text-xs text-stone-400">
                        You can navigate away. It&apos;ll show up in the Purchases list when ready.
                    </p>
                </div>
            </div>
        );
    }

    if (pi.status === "extraction_failed") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50 p-8" data-testid="pi-extraction-failed">
                <div className="bg-white border rounded-md p-8 max-w-md w-full text-center space-y-4"
                     style={{ borderColor: "#fecaca", background: "#fff1f2" }}>
                    <h2 className="font-serif text-2xl" style={{ color: "#9f1239" }}>Extraction failed</h2>
                    <p className="text-sm text-stone-600">
                        AI couldn&apos;t read this file: <br />
                        <code className="text-xs">{pi.extraction_error || "unknown error"}</code>
                    </p>
                    <div className="flex gap-2 justify-center pt-2">
                        <Button variant="outline" onClick={() => nav("/purchase")} data-testid="pi-back-list">
                            Back to list
                        </Button>
                        <Button onClick={discard} style={{ background: "#9f1239", color: "#fff" }} data-testid="pi-discard-failed">
                            Discard this upload
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setLine = (i, k, v) => setForm((f) => {
        const lines = f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l);
        // If qty or rate changed, autofill taxable = qty*rate and total = taxable + gst_amount
        if (k === "qty" || k === "rate") {
            const line = lines[i];
            const qty = Number(line.qty) || 0;
            const rate = Number(line.rate) || 0;
            if (qty > 0 && rate > 0) {
                const taxable = +(qty * rate).toFixed(2);
                line.taxable = taxable;
                const gstRate = Number(line.gst_rate) || 0;
                const gstAmt = +(taxable * gstRate / 100).toFixed(2);
                line.gst_amount = gstAmt;
                line.total = +(taxable + gstAmt).toFixed(2);
            }
        }
        // If gst_rate changed, recompute gst_amount and total from taxable
        if (k === "gst_rate") {
            const line = lines[i];
            const taxable = Number(line.taxable) || 0;
            const gstRate = Number(line.gst_rate) || 0;
            const gstAmt = +(taxable * gstRate / 100).toFixed(2);
            line.gst_amount = gstAmt;
            line.total = +(taxable + gstAmt).toFixed(2);
        }
        return { ...f, lines };
    });
    const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { description: "", hsn: "", qty: null, uom: "", rate: null, taxable: 0, gst_rate: 18, gst_amount: 0, total: 0, is_zero_tax: false }]}));
    const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i)}));

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form,
                subtotal: Number(form.subtotal) || 0,
                gst_amount: Number(form.gst_amount) || 0,
                cgst_amount: Number(form.cgst_amount) || 0,
                sgst_amount: Number(form.sgst_amount) || 0,
                igst_amount: Number(form.igst_amount) || 0,
                total: Number(form.total) || 0,
                inward_transport_amount: form.inward_transport_amount ? Number(form.inward_transport_amount) : null,
            };
            if (!payload.category_ledger_id && !payload.is_asset) { toast.error("Select a category"); setSaving(false); return; }
            if (payload.is_asset && !payload.asset_category_id) { toast.error("Pick an asset category to capitalise this purchase"); setSaving(false); return; }
            await api.patch(`/purchase-invoices/${id}`, payload);
            toast.success("Draft saved");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    const canPost = user?.role === "founder" || user?.role === "accountant";
    const confirm = async () => {
        if (!canPost) return toast.error("Only Founder / Accountant can post to ledgers. Save your changes — a reviewer will post it.");
        // (Sep 2026 policy) ITC is always posted — the 2B-check dialog is
        // deliberately removed. GSTR-2B mismatches are handled downstream
        // as a notification, not a capture-time gate.
        setSaving(true);
        try {
            const payload = { ...form,
                subtotal: Number(form.subtotal) || 0,
                gst_amount: Number(form.gst_amount) || 0,
                cgst_amount: Number(form.cgst_amount) || 0,
                sgst_amount: Number(form.sgst_amount) || 0,
                igst_amount: Number(form.igst_amount) || 0,
                total: Number(form.total) || 0,
                inward_transport_amount: form.inward_transport_amount ? Number(form.inward_transport_amount) : null,
                itc_confirmed: true,
            };
            if (!payload.category_ledger_id && !payload.is_asset) { toast.error("Select a category"); setSaving(false); return; }
            if (payload.is_asset && !payload.asset_category_id) { toast.error("Pick an asset category to capitalise this purchase"); setSaving(false); return; }
            await api.post(`/purchase-invoices/${id}/confirm`, payload);
            toast.success("Posted to ledgers");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    const reverse = async () => {
        const reason = prompt("Reason for reversal?");
        if (!reason) return;
        try {
            await api.post(`/purchase-invoices/${id}/reverse`, null, { params: { reason }});
            toast.success("Reversed");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const reverseAndEdit = async () => {
        const reason = prompt("Reason for editing? (e.g. wrong category)");
        if (!reason) return;
        try {
            await api.post(`/purchase-invoices/${id}/reverse`, null, { params: { reason, to_draft: true }});
            toast.success("Reversed — you can edit now and re-post");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const discard = async () => {
        if (!confirm("Discard this draft? The uploaded document will be marked deleted and cannot be recovered.")) return;
        try {
            await api.delete(`/purchase-invoices/${id}`);
            toast.success("Draft discarded");
            nav("/purchase");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const cancelInvoice = async () => {
        const reason = window.prompt("Why is this being cancelled? (e.g. vendor cancelled the order)");
        if (!reason) return;
        const noun = pi.doc_type === "debit_note" ? "debit note" : "invoice";
        if (!window.confirm(`Cancel this ${noun}? Its journals will be reversed and any linked debit notes will also be cancelled. This is different from Reverse & Edit — a cancelled ${noun} cannot be re-posted.`)) return;
        try {
            await api.post(`/purchase-invoices/${id}/cancel`, null, { params: { reason }});
            toast.success(`${noun[0].toUpperCase()}${noun.slice(1)} cancelled`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const isPosted = pi.status === "posted";
    const dupInfo = pi.duplicate_info;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title={`Purchase invoice`}
                sub={<>{pi.vendor_name || "—"} · Inv {pi.invoice_no || "—"}</>}
                right={
                    <>
                        <StatusChip status={pi.status} />
                        {pi.confidence != null && !isPosted ? <ConfidenceBadge value={pi.confidence} /> : null}
                        {pi.duplicate_of && !isPosted ? <span className="se-chip se-chip-err" data-testid="dup-badge">Possible duplicate</span> : null}
                        {isPosted && form.vendor_ledger_id ? (
                            <Link
                                to={`/ledgers/${form.vendor_ledger_id}`}
                                className="text-xs uppercase tracking-widest hover:underline flex items-center gap-1"
                                style={{ color: "var(--se-brand)" }}
                                data-testid="pi-vendor-ledger-link"
                            >
                                View vendor ledger →
                            </Link>
                        ) : null}
                    </>
                }
            />
            {dupInfo && !isPosted ? (
                <div className="mx-8 mt-6 border-l-4 rounded-sm p-4 flex items-start gap-3 flex-wrap" style={{ borderColor: "#991b1b", background: "var(--se-err-bg)", color: "var(--se-err-text)" }} data-testid="dup-banner">
                    <div className="flex-1 min-w-[260px]">
                        <div className="font-medium">This invoice looks already uploaded.</div>
                        <div className="text-xs mt-1">
                            <b>{dupInfo.vendor_name}</b> · Inv <b>{dupInfo.invoice_no}</b> · ₹{Number(dupInfo.total).toLocaleString("en-IN")}
                            {" · "}
                            <span className="uppercase tracking-widest">{dupInfo.status}</span>
                            {dupInfo.invoice_date ? <> · dated {dupInfo.invoice_date}</> : null}
                        </div>
                        <div className="text-xs mt-1">To avoid a double entry, please review the existing one and discard this draft if it&apos;s the same bill.</div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => nav(`/purchase/${dupInfo.id}`)} data-testid="dup-open-existing">Open existing</Button>
                        <Button size="sm" onClick={discard} style={{ background: "#991b1b", color: "#fff" }} data-testid="dup-discard">Discard this draft</Button>
                    </div>
                </div>
            ) : null}
            {recurringMatches.length > 0 && !recurringDismissed && !isPosted ? (
                <div className="mx-8 mt-6 border-l-4 rounded-sm p-4 flex items-start gap-3 flex-wrap"
                     style={{ borderColor: "#7c3aed", background: "#faf5ff", color: "#4c1d95" }}
                     data-testid="recurring-match-banner">
                    <div className="flex-1 min-w-[260px]">
                        <div className="font-medium">
                            Heads-up: this invoice looks like a recurring expense you&apos;ve already booked.
                        </div>
                        <div className="text-xs mt-2 space-y-1">
                            {recurringMatches.map((m) => (
                                <div key={m.recurring_expense_id} className="flex items-center gap-2 flex-wrap">
                                    <span>
                                        <b>{m.name}</b>
                                        {m.vendor_name ? ` · ${m.vendor_name}` : ""}
                                        {" · "}₹{Number(m.expected_amount).toLocaleString("en-IN")} expected
                                        {" · "}
                                        <span className="uppercase tracking-wider text-[9px] px-1 py-0.5 rounded bg-purple-100 text-purple-800">
                                            {m.billing_frequency}
                                        </span>
                                        {m.matched_cycle_period ? (
                                            <span className="text-[10px] text-purple-700"> · cycle {m.matched_cycle_period}</span>
                                        ) : null}
                                    </span>
                                    {m.matched_cycle_id ? (
                                        <Button size="sm" variant="outline"
                                                onClick={() => linkToRecurringCycle(m)}
                                                data-testid={`recurring-link-${m.recurring_expense_id}`}>
                                            Link to recurring cycle
                                        </Button>
                                    ) : (
                                        <Link to="/recurring-expenses"
                                              className="text-[10px] underline hover:text-purple-900"
                                              data-testid={`recurring-open-${m.recurring_expense_id}`}>
                                            Open recurring →
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="text-xs mt-2 text-purple-800">
                            If the physical PDF is just the source doc for a recurring you already accrued/prepaid, link it
                            and discard this draft — otherwise ignore this warning and confirm.
                        </div>
                    </div>
                    <div>
                        <Button size="sm" variant="ghost" onClick={() => setRecurringDismissed(true)}
                                data-testid="recurring-dismiss">
                            Dismiss
                        </Button>
                    </div>
                </div>
            ) : null}
            <div className="p-8 grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
                <div className="bg-white border rounded-md xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-auto"
                     style={{ borderColor: "var(--se-border)" }}
                     data-testid="pi-source-doc-sticky">
                    <div className="px-5 py-3 border-b text-xs uppercase tracking-widest text-stone-500 sticky top-0 bg-white z-10" style={{ borderColor: "var(--se-border)" }}>Source document</div>
                    <div className="p-5">
                        <SourceDocViewer documentId={pi.document_id} filename={pi.document_filename || `${pi.vendor_name || "invoice"}_${pi.invoice_no || pi.id}.pdf`} />
                    </div>
                </div>

                <div className="space-y-6">
                    {pi.auto_created_vendor && !form.no_vendor_ledger && !isPosted ? (
                        <div className="text-xs px-3 py-2 rounded-sm flex items-center gap-2" style={{ background: "var(--se-teal-soft)", color: "var(--se-teal)" }} data-testid="auto-vendor-badge">
                            <span>✓ Vendor ledger created automatically for <b>{pi.vendor_name}</b>. You can change or unlink below.</span>
                        </div>
                    ) : null}
                    {isPosted && form.vendor_ledger_id ? (
                        <div className="text-sm px-4 py-3 rounded-sm flex items-center justify-between" style={{ background: "var(--se-brand-soft)", color: "var(--se-brand)" }}>
                            <span>Posted to vendor <b>{pi.vendor_name}</b>. Follow the money in the ledger →</span>
                            <Link
                                to={`/ledgers/${form.vendor_ledger_id}`}
                                className="text-xs uppercase tracking-widest bg-white px-3 py-1.5 rounded-sm hover:opacity-80"
                                style={{ color: "var(--se-brand)" }}
                                data-testid="pi-open-vendor-ledger"
                            >
                                Open vendor ledger
                            </Link>
                        </div>
                    ) : null}
                    <div className="bg-white border rounded-md relative overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                        <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: form.no_vendor_ledger ? "var(--se-plum)" : "var(--se-brand)" }} />
                        <div className="px-5 py-3 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: "var(--se-border)" }}>
                            <div className="text-xs uppercase tracking-widest" style={{ color: form.no_vendor_ledger ? "var(--se-plum)" : "var(--se-brand)" }}>
                                {form.no_vendor_ledger ? "Petty expense · pay from Cash / Bank" : "Vendor & header"}
                            </div>
                            <label className="text-xs flex items-center gap-2 cursor-pointer">
                                <Checkbox checked={form.no_vendor_ledger} onCheckedChange={(v) => set("no_vendor_ledger", !!v)} data-testid="pi-petty-toggle" />
                                <span>Petty / one-off expense — no vendor ledger needed</span>
                            </label>
                        </div>
                        <div className="p-5 grid grid-cols-2 gap-4">
                            {!form.no_vendor_ledger ? (
                                <>
                                    <div className="col-span-2">
                                        <Label>Vendor</Label>
                                        <ComboSelect
                                            value={form.vendor_ledger_id || ""}
                                            onValueChange={(v) => set("vendor_ledger_id", v === "__new__" ? "" : v)}
                                            options={[
                                                { id: "__new__", name: "+ Add new vendor from extracted name" },
                                                ...vendors.map((v) => ({ id: v.id, name: v.name, hint: v.gstin || undefined }))
                                            ]}
                                            placeholder="Select or add"
                                            testId="pi-vendor-select"
                                        />
                                    </div>
                                    <div><Label>Vendor name (new)</Label><Input value={form.vendor_name || ""} disabled={!!form.vendor_ledger_id} onChange={(e) => set("vendor_name", e.target.value)} data-testid="pi-vendor-name" /></div>
                                    <div><Label>Vendor GSTIN</Label><Input value={form.vendor_gstin || ""} disabled={!!form.vendor_ledger_id} onChange={(e) => set("vendor_gstin", e.target.value)} data-testid="pi-vendor-gstin" /></div>
                                </>
                            ) : (
                                <div className="col-span-2">
                                    <Label>Paid from (Cash / Bank)</Label>
                                    <Select value={form.payment_ledger_id || ""} onValueChange={(v) => set("payment_ledger_id", v)}>
                                        <SelectTrigger data-testid="pi-payment-ledger"><SelectValue placeholder="Choose Cash or Bank" /></SelectTrigger>
                                        <SelectContent>{paymentLedgers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <p className="text-xs text-stone-500 mt-1">Amount is deducted directly from this account — no payable is created.</p>
                                </div>
                            )}
                            <div><Label>Invoice no.</Label><Input value={form.invoice_no || ""} onChange={(e) => set("invoice_no", e.target.value)} data-testid="pi-invno" /></div>
                            <div><Label>Invoice date</Label><Input type="date" value={form.invoice_date || ""} onChange={(e) => set("invoice_date", e.target.value)} data-testid="pi-invdate" /></div>
                            <div className="col-span-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <Label className="text-[13px] font-medium" style={{ color: "var(--se-brand)" }}>
                                        Category *
                                    </Label>
                                    {(() => {
                                        const picked = categories.find((c) => c.id === form.category_ledger_id);
                                        if (!picked) return null;
                                        const cat = picked._group_category
                                                     ?? groups?.find?.((g) => g.id === picked.group_id)?.category;
                                        if (!cat) return null;
                                        const isDirect = cat === "direct_cost";
                                        return (
                                            <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-medium"
                                                  style={{
                                                      background: isDirect ? "#dcfce7" : "#fef3c7",
                                                      color:      isDirect ? "#166534" : "#92400e",
                                                  }}
                                                  data-testid="pi-direct-indirect-badge">
                                                {isDirect ? "Direct cost" : "Indirect expense"}
                                            </span>
                                        );
                                    })()}
                                </div>
                                <ComboSelect
                                    value={form.category_ledger_id || ""}
                                    onValueChange={(v) => {
                                        set("category_ledger_id", v);
                                        // Auto-derive direct/indirect from the picked category's group
                                        const picked = categories.find((c) => c.id === v);
                                        const cat = picked?._group_category
                                                     ?? groups?.find?.((g) => g.id === picked?.group_id)?.category;
                                        if (cat) set("direct_indirect", cat === "direct_cost" ? "direct" : "indirect");
                                    }}
                                    options={categories.map((c) => ({ id: c.id, name: c.name, hint: c.group_name }))}
                                    placeholder="Choose expense head (this drives P&L and Unit Economics)"
                                    testId="pi-category"
                                />
                                <p className="text-[11px] text-stone-500 mt-1">
                                    Direct/Indirect is inferred from the group. Change it by picking a different category (or edit the ledger&apos;s parent group).
                                </p>
                                {pi.extracted?.suggested_category ? (
                                    <p className="text-[11px] mt-1" style={{ color: "var(--se-brand)" }} data-testid="pi-ai-category-hint">
                                        <span className="mr-1">🪄</span>
                                        AI read this invoice as a <b>{pi.extracted.suggested_category}</b> expense
                                        {form.category_ledger_id ? " — auto-matched above. Change if wrong." : " — pick the closest category above."}
                                    </p>
                                ) : null}
                            </div>
                            <div>
                                <Label>Department <span className="text-stone-400 font-normal">(optional)</span></Label>
                                <Select value={form.dept || ""} onValueChange={(v) => set("dept", v)}>
                                    <SelectTrigger data-testid="pi-dept"><SelectValue placeholder="Choose (or leave blank)" /></SelectTrigger>
                                    <SelectContent>
                                        {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <p className="text-[11px] text-stone-500 mt-1">Only tag when the cost belongs to one team&apos;s budget (e.g. Marketing agency fee). Skip for shared raw materials.</p>
                            </div>
                            <div>
                                <Label>Location</Label>
                                <Select value={form.location || ""} onValueChange={(v) => set("location", v)}>
                                    <SelectTrigger data-testid="pi-location"><SelectValue placeholder="Choose" /></SelectTrigger>
                                    <SelectContent>
                                        {locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-2 flex items-center gap-2 pt-2">
                                <Checkbox id="expnp" checked={form.is_expense_not_purchase} onCheckedChange={(v) => set("is_expense_not_purchase", !!v)} data-testid="pi-is-expense" />
                                <Label htmlFor="expnp" className="cursor-pointer text-sm text-stone-600">This is a service / utility invoice, not raw material</Label>
                            </div>
                            <div className="col-span-2 flex items-center gap-2">
                                <Checkbox id="svc" checked={form.is_service_purchase} onCheckedChange={(v) => {
                                    const on = !!v;
                                    setForm((f) => {
                                        if (!on) {
                                            return { ...f, is_service_purchase: false, vendor_type: "", tds_rate: 0, tds_amount: 0 };
                                        }
                                        // default 2% (company) — user can flip
                                        const base = Number(f.subtotal) || 0;
                                        const rate = f.tds_rate || 2;
                                        return {
                                            ...f,
                                            is_service_purchase: true,
                                            vendor_type: f.vendor_type || "company",
                                            tds_rate: rate,
                                            tds_amount: +(base * rate / 100).toFixed(2),
                                        };
                                    });
                                }} data-testid="pi-is-service" />
                                <Label htmlFor="svc" className="cursor-pointer text-sm text-stone-600">
                                    Service / jobwork purchase — deduct TDS on payment
                                </Label>
                            </div>
                            <div className="col-span-2 flex items-center gap-2">
                                <Checkbox id="rcm-inv" checked={form.rcm_applies} onCheckedChange={(v) => {
                                    const on = !!v;
                                    setForm((f) => ({
                                        ...f,
                                        rcm_applies: on,
                                        rcm_rate: on ? (f.rcm_rate || 18) : 0,
                                    }));
                                }} data-testid="pi-rcm-invoice" />
                                <Label htmlFor="rcm-inv" className="cursor-pointer text-sm text-stone-600">
                                    Whole invoice under RCM — I self-pay GST (common for rent / unregistered vendors)
                                </Label>
                            </div>
                            <div className="col-span-2 flex items-center gap-2">
                                <Checkbox id="asset" checked={form.is_asset} onCheckedChange={(v) => {
                                    const on = !!v;
                                    setForm((f) => ({
                                        ...f,
                                        is_asset: on,
                                        asset_put_to_use_date: on ? (f.asset_put_to_use_date || f.invoice_date || "") : "",
                                        asset_name: on ? (f.asset_name || `${f.vendor_name || "Vendor"} — Inv ${f.invoice_no || "-"}`) : "",
                                    }));
                                }} data-testid="pi-is-asset" />
                                <Label htmlFor="asset" className="cursor-pointer text-sm text-stone-600">
                                    <b>This is a fixed asset (capex)</b> — capitalise to balance sheet, don&apos;t hit P&amp;L
                                </Label>
                            </div>
                            {form.is_asset ? (
                                <div className="col-span-2 border rounded-md p-3 bg-indigo-50/40 space-y-3" style={{ borderColor: "#c7d2fe" }}>
                                    <div className="text-[11px] text-stone-600">
                                        The <b>Category</b> above is ignored for asset purchases — instead the money goes to <b>Fixed Asset — {"<Asset category>"}</b> on the balance sheet, and a row is auto-created in the Fixed Asset Register so monthly depreciation starts flowing.
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Asset category *</Label>
                                            <Select value={form.asset_category_id || ""} onValueChange={(v) => set("asset_category_id", v)}>
                                                <SelectTrigger data-testid="pi-asset-category"><SelectValue placeholder="Pick asset category" /></SelectTrigger>
                                                <SelectContent>
                                                    {assetCategories.map((c) => (
                                                        <SelectItem key={c.id} value={c.id}>
                                                            {c.name} · {c.default_wdv_rate_pct}% WDV
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className="text-[10px] text-stone-500 mt-1">Drives which Fixed Asset ledger the money hits and the default depreciation rate.</p>
                                        </div>
                                        <div>
                                            <Label>Put-to-use date</Label>
                                            <Input type="date" value={form.asset_put_to_use_date || ""}
                                                   onChange={(e) => set("asset_put_to_use_date", e.target.value)}
                                                   data-testid="pi-asset-put-use" />
                                            <p className="text-[10px] text-stone-500 mt-1">Depreciation starts from this month (defaults to invoice date).</p>
                                        </div>
                                        <div className="col-span-2">
                                            <Label>Asset name</Label>
                                            <Input value={form.asset_name || ""}
                                                   onChange={(e) => set("asset_name", e.target.value)}
                                                   placeholder="e.g. 1.5T AC — Head Office"
                                                   data-testid="pi-asset-name" />
                                        </div>
                                        <div className="col-span-2 flex items-start gap-2">
                                            <Checkbox id="itc" checked={form.itc_claimed_on_asset}
                                                      onCheckedChange={(v) => set("itc_claimed_on_asset", !!v)}
                                                      data-testid="pi-asset-itc" />
                                            <Label htmlFor="itc" className="cursor-pointer text-sm text-stone-600">
                                                <div><b>Claim ITC on GST</b> (default ON)</div>
                                                <div className="text-[10px] text-stone-500">
                                                    ON: depreciable base = ex-GST value (₹{inr(form.subtotal || 0)}). GST flows to Input CGST/SGST as usual.
                                                    <br />OFF: GST is blocked (e.g. motor cars for personal use) — the entire ₹{inr(form.total || 0)} gets capitalised.
                                                </div>
                                            </Label>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {form.rcm_applies ? (
                        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="rcm-invoice-card">
                            <div className="px-5 py-3 border-b text-xs uppercase tracking-widest flex items-center justify-between" style={{ borderColor: "var(--se-border)", color: "#854d0e" }}>
                                <span>RCM — Reverse Charge on this invoice</span>
                                <a href="/settings" className="text-[11px] normal-case tracking-normal text-stone-500 hover:underline">Edit default rates in Settings</a>
                            </div>
                            <div className="p-5 grid grid-cols-4 gap-4 items-end">
                                <div>
                                    <Label>RCM rate</Label>
                                    <Select value={String(form.rcm_rate || 18)} onValueChange={(v) => set("rcm_rate", Number(v))}>
                                        <SelectTrigger data-testid="pi-rcm-rate"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="5">5% — Transporter (GTA)</SelectItem>
                                            <SelectItem value="18">18% — Rent / professional</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>RCM tax amount (₹)</Label>
                                    <Input type="number" readOnly value={((Number(form.subtotal) || 0) * (Number(form.rcm_rate) || 0) / 100).toFixed(2)} data-testid="pi-rcm-tax" />
                                    <p className="text-[11px] text-stone-500 mt-1">= subtotal × rate. Auto-computed.</p>
                                </div>
                                <div className="col-span-2 text-[11px] text-stone-500 leading-relaxed">
                                    We&apos;ll Cr <b>RCM Payable @ {form.rcm_rate || 18}%</b> for this amount (you owe it to the government) and
                                    simultaneously Dr <b>RCM ITC @ {form.rcm_rate || 18}%</b> so the same amount is claimable back
                                    in this month&apos;s GSTR. Both appear on the Net GST page.
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {form.is_service_purchase ? (
                        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="tds-card">
                            <div className="px-5 py-3 border-b text-xs uppercase tracking-widest" style={{ borderColor: "var(--se-border)", color: "var(--se-plum)" }}>
                                Jobwork / Service — TDS (Section 194C)
                            </div>
                            <div className="p-5 grid grid-cols-4 gap-4 items-end">
                                <div>
                                    <Label>Vendor type</Label>
                                    <Select value={form.vendor_type || "company"} onValueChange={(v) => {
                                        const rate = v === "individual" ? 1 : 2;
                                        const base = Number(form.subtotal) || 0;
                                        setForm((f) => ({ ...f, vendor_type: v, tds_rate: rate, tds_amount: +(base * rate / 100).toFixed(2) }));
                                    }}>
                                        <SelectTrigger data-testid="pi-vendor-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="individual">Individual / HUF (1%)</SelectItem>
                                            <SelectItem value="company">Company / Firm (2%)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>TDS rate (%)</Label>
                                    <Input type="number" value={form.tds_rate ?? 0} onChange={(e) => {
                                        const rate = Number(e.target.value) || 0;
                                        const base = Number(form.subtotal) || 0;
                                        setForm((f) => ({ ...f, tds_rate: rate, tds_amount: +(base * rate / 100).toFixed(2) }));
                                    }} data-testid="pi-tds-rate" />
                                </div>
                                <div>
                                    <Label>TDS amount (₹)</Label>
                                    <Input type="number" value={form.tds_amount ?? 0} onChange={(e) => set("tds_amount", Number(e.target.value) || 0)} data-testid="pi-tds-amount" />
                                    <p className="text-[11px] text-stone-500 mt-1">= subtotal × rate. Editable.</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-[11px] uppercase tracking-widest text-stone-500">Vendor payable</div>
                                    <div className="text-xl font-serif" style={{ color: "var(--se-brand)" }} data-testid="pi-vendor-payable">
                                        ₹ {inr((Number(form.total) || 0) - (Number(form.tds_amount) || 0))}
                                    </div>
                                    <div className="text-[11px] text-stone-500">Total ₹{inr(form.total)} − TDS ₹{inr(form.tds_amount)}</div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                            <div className="text-xs uppercase tracking-widest text-stone-500">Line items</div>
                            <Button variant="ghost" size="sm" onClick={addLine} data-testid="pi-add-line">+ Add line</Button>
                        </div>
                        <div className="p-3 overflow-x-auto">
                            <table className="se-table" style={{ minWidth: 900 }}>
                                <thead><tr><th>Description</th><th>HSN</th><th className="text-right">Qty</th><th>UoM</th><th className="text-right">Rate</th><th className="text-right">Taxable</th><th className="text-right">GST %</th><th className="text-right">GST ₹</th><th className="text-right">Total</th><th>Zero</th><th></th></tr></thead>
                                <tbody>
                                    {form.lines.map((l, i) => (
                                        <tr key={i}>
                                            <td><Input value={l.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} className="h-8 text-xs min-w-[140px]" data-testid={`line-desc-${i}`} /></td>
                                            <td><Input value={l.hsn || ""} onChange={(e) => setLine(i, "hsn", e.target.value)} className="h-8 text-xs w-20" data-testid={`line-hsn-${i}`} /></td>
                                            <td><Input value={l.qty ?? ""} onChange={(e) => setLine(i, "qty", e.target.value === "" ? null : Number(e.target.value))} className="h-8 text-xs w-20 text-right" data-testid={`line-qty-${i}`} /></td>
                                            <td><Input value={l.uom || ""} onChange={(e) => setLine(i, "uom", e.target.value)} placeholder="Nos" className="h-8 text-xs w-16" data-testid={`line-uom-${i}`} /></td>
                                            <td><Input value={l.rate ?? ""} onChange={(e) => setLine(i, "rate", e.target.value === "" ? null : Number(e.target.value))} className="h-8 text-xs w-24 text-right" data-testid={`line-rate-${i}`} /></td>
                                            <td><Input value={l.taxable ?? ""} onChange={(e) => setLine(i, "taxable", Number(e.target.value))} className="h-8 text-xs w-24 text-right" /></td>
                                            <td><Input value={l.gst_rate ?? ""} onChange={(e) => setLine(i, "gst_rate", Number(e.target.value))} className="h-8 text-xs w-14 text-right" /></td>
                                            <td><Input value={l.gst_amount ?? ""} onChange={(e) => setLine(i, "gst_amount", Number(e.target.value))} className="h-8 text-xs w-20 text-right" /></td>
                                            <td><Input value={l.total ?? ""} onChange={(e) => setLine(i, "total", Number(e.target.value))} className="h-8 text-xs w-24 text-right" /></td>
                                            <td><Checkbox checked={!!l.is_zero_tax} onCheckedChange={(v) => setLine(i, "is_zero_tax", !!v)} /></td>
                                            <td><button className="text-xs text-stone-400 hover:text-red-700" onClick={() => removeLine(i)} data-testid={`line-remove-${i}`}>×</button></td>
                                        </tr>
                                    ))}
                                    {form.lines.length === 0 ? <tr><td colSpan="11" className="text-stone-400 py-4 text-center">No lines extracted — add manually or re-upload.</td></tr> : null}
                                </tbody>
                            </table>
                            <p className="text-xs text-stone-500 mt-3">Tip: enter <b>Qty × Rate</b> and taxable + total update automatically. UoM (Nos, Kg, Sq.ft…) is stored per line so we can drive stock analytics later.</p>
                            <div className="grid grid-cols-3 gap-4 mt-5 pt-4 border-t" style={{ borderColor: "var(--se-border)" }}>
                                <div><Label>Subtotal</Label><Input type="number" value={form.subtotal} onChange={(e) => {
                                    const st = Number(e.target.value) || 0;
                                    setForm((f) => {
                                        const upd = { ...f, subtotal: e.target.value };
                                        if (f.is_service_purchase) {
                                            const rate = Number(f.tds_rate) || 0;
                                            upd.tds_amount = +(st * rate / 100).toFixed(2);
                                        }
                                        return upd;
                                    });
                                }} data-testid="pi-subtotal" /></div>
                                <div><Label>GST amount (total)</Label><Input type="number" value={form.gst_amount} onChange={(e) => set("gst_amount", e.target.value)} data-testid="pi-gst" /></div>
                                <div><Label>Total</Label><Input type="number" value={form.total} onChange={(e) => set("total", e.target.value)} data-testid="pi-total" /></div>
                                <div className="col-span-3 grid grid-cols-3 gap-4 pt-2 border-t" style={{ borderColor: "var(--se-border)" }}>
                                    <div><Label className="text-[11px] uppercase tracking-widest text-stone-500">CGST</Label><Input type="number" value={form.cgst_amount ?? 0} onChange={(e) => set("cgst_amount", e.target.value)} data-testid="pi-cgst" /></div>
                                    <div><Label className="text-[11px] uppercase tracking-widest text-stone-500">SGST</Label><Input type="number" value={form.sgst_amount ?? 0} onChange={(e) => set("sgst_amount", e.target.value)} data-testid="pi-sgst" /></div>
                                    <div><Label className="text-[11px] uppercase tracking-widest text-stone-500">IGST</Label><Input type="number" value={form.igst_amount ?? 0} onChange={(e) => set("igst_amount", e.target.value)} data-testid="pi-igst" /></div>
                                    <p className="col-span-3 text-[11px] text-stone-500">Post either CGST+SGST (intra-state, e.g. Karnataka→Karnataka) or IGST (inter-state). Sum should equal the GST total.</p>
                                </div>
                                {/* ITC-confirm gate REMOVED (Sep 2026 policy):
                                    always post Input CGST/SGST/IGST. Vendor
                                    filing status is surfaced separately by
                                    the GSTR-2B recon module. */}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-5 py-3 border-b text-xs uppercase tracking-widest text-stone-500" style={{ borderColor: "var(--se-border)" }}>Inward transport (optional)</div>
                        <div className="p-5 grid grid-cols-2 gap-4">
                            <div>
                                <Label>Transporter (existing)</Label>
                                <ComboSelect
                                    value={form.inward_transport_transporter_ledger_id || ""}
                                    onValueChange={(v) => set("inward_transport_transporter_ledger_id", v === "__new__" ? "" : v)}
                                    options={[
                                        { id: "__new__", name: "+ Add new" },
                                        ...transporters.map((t) => ({ id: t.id, name: t.name }))
                                    ]}
                                    placeholder="Choose or add new"
                                    testId="pi-transporter"
                                />
                            </div>
                            <div><Label>Or new transporter name</Label><Input value={form.inward_transport_transporter_name || ""} disabled={!!form.inward_transport_transporter_ledger_id} onChange={(e) => set("inward_transport_transporter_name", e.target.value)} data-testid="pi-transporter-new" /></div>
                            <div><Label>Freight amount</Label><Input type="number" value={form.inward_transport_amount ?? ""} onChange={(e) => set("inward_transport_amount", e.target.value)} data-testid="pi-freight" /></div>
                            <div className="col-span-3 text-[11px] text-stone-500 -mt-2">
                                RCM is applied automatically based on the transporter&apos;s default (Settings → Transporters).
                                {(() => {
                                    const tid = form.inward_transport_transporter_ledger_id;
                                    const t = transporters.find((x) => x.id === tid);
                                    if (!t) return null;
                                    return t.rcm_default
                                        ? <> · This transporter is <b>set to RCM</b> — freight GST will self-pay + ITC.</>
                                        : <> · This transporter is <b>NOT under RCM</b>.</>;
                                })()}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                        {!isPosted ? (
                            <>
                                {!canPost ? (
                                    <div className="text-[11px] px-3 py-2 rounded-sm bg-amber-50 border border-amber-300 text-amber-900" data-testid="pi-review-hint">
                                        You can edit and save the draft. A Founder or Accountant will review and post it.
                                    </div>
                                ) : null}
                                <Button variant="outline" onClick={save} disabled={saving} data-testid="pi-save-draft">Save draft</Button>
                                {canPost ? (
                                    <Button onClick={confirm} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="pi-confirm">
                                        Review & Post
                                    </Button>
                                ) : null}
                                <Button variant="ghost" onClick={discard} style={{ color: "#991b1b" }} data-testid="pi-discard">Discard draft</Button>
                            </>
                        ) : (
                            <>
                                <Button onClick={reverseAndEdit} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="pi-reverse-edit">Reverse & Edit</Button>
                                <Button variant="outline" onClick={reverse} data-testid="pi-reverse">Reverse posted entry</Button>
                                <Button variant="outline" onClick={cancelInvoice} style={{ borderColor: "#e11d48", color: "#9f1239" }} data-testid="pi-cancel">Cancel</Button>
                            </>
                        )}
                        <Button variant="ghost" onClick={() => nav("/purchase")} data-testid="pi-back">Back to list</Button>
                    </div>
                </div>
            </div>
        </>
    );
}
